import { useCallback, useMemo, useRef, useState } from "react";
import useGlobalContext from "../../../hooks/useGlobalContext.js";
import { getModalTitle } from "../FlightDetailsInner/components/flightDetailsUtils.js";
import { ACCOMMODATIONS_DOMAIN_URL, ACCOMMODATIONS_DOMAIN_URL_DQS } from "../../../constants.js";
import { useI18n, t } from "@bookingcom/lingojs-react";
import useEvents from "../../../hooks/useEvents.js";
import { isOfMetaOrigin } from "../../../utils/marketing-url-params.js";
import { clientFetch } from "@flights/client-fetch";
import useContextName from "../../../hooks/useContextName.js";
import { BOOKING_DEFAULT_AFFILIATE_ID, FLIGHTS_SHARE_DIRECT_LABEL, FLIGHTS_SHARE_META_LABEL } from "@flights/constants";
import { isFeatureRunningClientSide } from "@flights/web-api-utils-universal";
import { useSelector } from "react-redux";
import { getSearchType } from "../../../store/search/selectors.js";
export function useShareFlight(props) {
    const { flight } = props;
    const [sharePopoverActive, setSharePopoverActive] = useState(false);
    const [linkCopied, setLinkCopied] = useState(false);
    const [fetchedUrl, setFetchedUrl] = useState("");
    const i18n = useI18n();
    const searchType = useSelector(getSearchType);
    const contextName = useContextName();
    const { env, userAgent } = useGlobalContext();
    const shareButtonRef = useRef(null);
    const { jsError } = useEvents();
    const isMeta = isOfMetaOrigin();
    const copyToClipboard = useCallback((url) => {
        navigator.clipboard
            .writeText(url)
            .then(() => {
            setLinkCopied(true);
        })
            .catch((err) => {
            jsError(err);
        });
    }, [jsError]);
    const getShareUrl = useCallback(() => {
        const searchParams = new URLSearchParams(window.location.search);
        for (const param of searchParams.keys()) {
            if (param.startsWith("ext-")) {
                searchParams.delete(param);
            }
        }
        if (!isFeatureRunningClientSide("FLIGHTS_PRICING_POPULATE_AFFILIATE_ID_FOR_LINK_SHARING")) {
            searchParams.set("aid", BOOKING_DEFAULT_AFFILIATE_ID);
        }
        searchParams.set("label", isMeta ? FLIGHTS_SHARE_META_LABEL : FLIGHTS_SHARE_DIRECT_LABEL);
        return new URL(`${location.pathname}?${searchParams.toString()}`, location.origin).toString();
    }, [isMeta]);
    const nativeShareTitle = useMemo(() => {
        return flight?.segments
            .map(({ departureAirport, arrivalAirport }) => `${departureAirport.cityName} – ${arrivalAirport.cityName}`)
            .join(" | ");
    }, [flight?.segments]);
    const shareData = useMemo(() => ({
        title: nativeShareTitle,
        text: getModalTitle(i18n, flight?.segments, searchType),
        url: ""
    }), [flight?.segments, i18n, nativeShareTitle, searchType]);
    const onShare = useCallback(async () => {
        if (!fetchedUrl) {
            if (env !== "prod") {
                setFetchedUrl(getShareUrl());
                shareData.url = getShareUrl();
            }
            else {
                const response = await clientFetch("/api/urlSharing", {
                    method: "post",
                    body: JSON.stringify({
                        url: getShareUrl()
                    }),
                    headers: {
                        "Content-Type": "application/json",
                        "X-Flights-Context-Name": contextName
                    }
                });
                const shortUrl = response?.id
                    ? `${env === "prod" ? ACCOMMODATIONS_DOMAIN_URL : ACCOMMODATIONS_DOMAIN_URL_DQS}/${response.id}`
                    : getShareUrl();
                setFetchedUrl(shortUrl);
                shareData.url = shortUrl;
            }
        }
        else {
            shareData.url = fetchedUrl;
        }
        if (userAgent.isMobile && window.navigator?.canShare?.(shareData)) {
            window.navigator
                .share(shareData)
                .then()
                .catch((err) => {
                jsError(err);
            });
        }
        else {
            setSharePopoverActive(true);
        }
    }, [contextName, env, fetchedUrl, getShareUrl, jsError, shareData, userAgent.isMobile]);
    const openMail = useCallback((url) => {
        window.open(`mailto:?subject=${i18n.trans(t("flights_meta_fd_share_msg"))}&body=${encodeURIComponent(url)}`);
    }, [i18n]);
    const handleMailClick = useCallback(async () => {
        openMail(fetchedUrl);
    }, [fetchedUrl, openMail]);
    const handleCopyClick = useCallback(async () => {
        copyToClipboard(fetchedUrl);
    }, [copyToClipboard, fetchedUrl]);
    return {
        onShare,
        shareButtonRef,
        sharePopoverActive,
        setLinkCopied,
        setSharePopoverActive,
        linkCopied,
        fetchedUrl,
        handleCopyClick,
        handleMailClick
    };
}
